import type { ViewportSeriesData } from '@dynamika-shared/services/communication';
import type {
  ContextChangedPayload,
  DetachNoticePayload,
  OperationFailedPayload,
} from '@restore/readerViewerSession/tabCoordination/tabCoordination.types';
import type { ChangeScoringFormDataAction } from '@restore/readerViewerSession/formManagement/formManagement.types';
import type { Handler } from '@dynamika/rpc-core/engine';
import { getPeer } from '../peer';
import { DetachRpc } from '@dynamika/rpc-core/contracts';
import { requestMain, requestDetached, emitToDetached, emitToMain } from '../helpers';

/** Params the detached tab sends with `d7.detach.initialize`. */
type DetachInitializeParams = Record<string, never>;

type DetachListener<T> = (payload: T) => void;

const on = <T>(event: string, cb: DetachListener<T>) =>
  getPeer().on(event, payload => cb(payload as T));

export type DetachReleaseResponse = DetachInitializeResponse & {
  isValid?: boolean;
};

export type DetachInitializeResponse = {
  formData?: object;
  isDirty: boolean;
  lastSeries: ViewportSeriesData[] | null;
  mainWindowName: string;
};

export const detachRpcScheme = {
  handlers: {
    onInitialize: (cb: Handler) =>
      getPeer().handle(DetachRpc.Methods.INITIALIZE, cb),
    onReleaseForm: (cb: Handler) =>
      getPeer().handle(DetachRpc.Methods.RELEASE_FORM, cb),
  },
  requests: {
    initialize: (mainWindowName: string, timeoutMs?: number) => {
      const params: DetachInitializeParams = {};

      return requestMain<DetachInitializeResponse>(
        DetachRpc.Methods.INITIALIZE,
        params,
        { mainWindowName, timeoutMs }
      );
    },
    releaseForm: (timeoutMs?: number): Promise<DetachReleaseResponse> =>
      requestDetached(DetachRpc.Methods.RELEASE_FORM, {}, timeoutMs),
    /**
     * Asks whether the detached host peer is still there, changing nothing.
     *
     * `rpc.negotiate` is the engine's own method. A live peer always answers an
     * addressed request — with capabilities, or `-32601 Method not found` when
     * it registered none — so only a gone peer ends in the `-32000` timeout.
     */
    probe: (timeoutMs: number): Promise<unknown> =>
      requestDetached('rpc.negotiate', {}, timeoutMs),
  },
  events: {
    deactivate: (payload: Record<string, never>) =>
      emitToDetached(DetachRpc.Events.DEACTIVATE, payload),
    notice: (payload: DetachNoticePayload) =>
      emitToDetached(DetachRpc.Events.NOTICE, payload),
    operationFailed: (payload: OperationFailedPayload) =>
      emitToDetached(DetachRpc.Events.OPERATION_FAILED, payload),
    contextChanged: (payload: ContextChangedPayload) =>
      emitToMain(DetachRpc.Events.CONTEXT_CHANGED, payload),
    formChanged: (payload: ChangeScoringFormDataAction['payload']) =>
      emitToMain(DetachRpc.Events.FORM_CHANGED, payload),
    completeAttach: (payload: Record<string, never>) =>
      emitToDetached(DetachRpc.Events.ATTACH_COMPLETED, payload),
  },
  /**
   * `on`, not `handle`.
   *
   * Everything in `events` above is sent with `emit` — a JSON-RPC notification,
   * no `id`, no reply expected. `peer.handle` registers a method for `request`
   * envelopes and is never consulted for a notification, so subscribing that
   * way silently receives nothing at all.
   */
  listeners: {
    onDeactivate: (cb: DetachListener<Record<string, never>>) =>
      on(DetachRpc.Events.DEACTIVATE, cb),
    onNotice: (cb: DetachListener<DetachNoticePayload>) =>
      on(DetachRpc.Events.NOTICE, cb),
    onOperationFailed: (cb: DetachListener<OperationFailedPayload>) =>
      on(DetachRpc.Events.OPERATION_FAILED, cb),
    onContextChanged: (cb: DetachListener<ContextChangedPayload>) =>
      on(DetachRpc.Events.CONTEXT_CHANGED, cb),
    onFormChanged: (cb: DetachListener<ChangeScoringFormDataAction['payload']>) =>
      on(DetachRpc.Events.FORM_CHANGED, cb),
    onAttachCompleted: (cb: DetachListener<Record<string, never>>) =>
      on(DetachRpc.Events.ATTACH_COMPLETED, cb),
  },
};
